// src/components/SalariesSummary/SalariesSummary.js

import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast";
import styles from "./SalariesSummary.module.css";
import {
  FaArrowLeft,
  FaFileInvoiceDollar,
  FaPen,
  FaTrash,
  FaMoneyBillWave,
  FaHandHoldingUsd,
  FaBalanceScale,
  FaFileInvoice,
} from "react-icons/fa";
import { DeleteModal } from "../Fields/FieldDetails";

const TransactionItem = ({ transaction, isRTL, onEdit, onDelete }) => {
  const { t, i18n } = useTranslation();

  const getTransactionDetails = (type) => {
    switch (type) {
      case "salaryPayment":
        return {
          title: t("Salary Payment"),
          sign: "+",
          styleClass: styles.amountPositive,
        };
      case "salaryAdvance":
        return {
          title: t("Advance from Salary"),
          sign: "+",
          styleClass: styles.amountPositive,
        };
      case "salaryDeduction":
        return {
          title: t("Deduction from Salary"),
          sign: "-",
          styleClass: styles.amountNegative,
        };
      default:
        return { title: type, sign: "", styleClass: "" };
    }
  };

  const { title, sign, styleClass } = getTransactionDetails(
    transaction.payment.type
  );
  const formattedDate = new Date(
    transaction.payment.paymentDate
  ).toLocaleDateString(i18n.language === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className={styles.transactionItem}>
      {/* --- Group 1: All textual details --- */}
      <div className={styles.transactionDetails}>
        <h3 className={styles.transactionTitle}>{title}</h3>
        <div className={styles.transactionMeta}>
          <span className={styles.employeeName}>
            {transaction.employee?.username || t("N/A")}
          </span>
          <span className={styles.transactionDate}>{formattedDate}</span>
        </div>
        {transaction.payment.description && (
          <p className={styles.transactionDescription}>
            {transaction.payment.description}
          </p>
        )}
      </div>

      {/* --- Group 2: The value and the actions --- */}
      <div className={styles.transactionValue}>
        <span className={styleClass}>
          {sign} {transaction.payment.amountPaid?.toLocaleString(i18n.language)}
        </span>
        <div className={styles.transactionActions}>
          <button
            className={styles.actionButton}
            aria-label={t("Edit")}
            onClick={() => onEdit(transaction)}
          >
            <FaPen />
          </button>
          <button
            className={`${styles.actionButton} ${styles.deleteButton}`}
            aria-label={t("Delete")}
            onClick={() => onDelete(transaction)}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
};

const SalariesSummary = () => {
  const { t, i18n } = useTranslation();
  const { token, userData } = useContext(UserContext);
  const navigate = useNavigate();
  const monthSliderRef = useRef(null);

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState([]);
  const [activeMonth, setActiveMonth] = useState(new Date().getMonth());

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // --- Month Slider Setup ---
  useEffect(() => {
    const date = new Date();
    const monthObjects = [];
    const currentLocale = i18n.language === "ar" ? "ar-EG" : "en-US";
    for (let i = 0; i < 12; i++) {
      const tempDate = new Date(date.getFullYear(), i, 1);
      monthObjects.push({
        name: tempDate.toLocaleString(currentLocale, { month: "long" }),
        monthIndex: i,
      });
    }
    setMonths(monthObjects);
    setActiveMonth(date.getMonth());
  }, [i18n.language]);

  useEffect(() => {
    if (monthSliderRef.current) {
      const activeElement =
        monthSliderRef.current.querySelector(`[data-active="true"]`);
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeMonth, months]);

  // --- Data Fetching (Simplified) ---
  const fetchSalariesSummaryData = useCallback(async () => {
    if (!token || !userData?.Farm?.id) {
      setLoading(false);
      setError(t("Authentication token or Farm ID not found."));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const currentYear = new Date().getFullYear();
      const firstDay = new Date(currentYear, activeMonth, 1);
      const lastDay = new Date(currentYear, activeMonth + 1, 0);
      const fromTimestamp = firstDay.getTime();
      const toTimestamp = lastDay.getTime();
      const farmId = userData.Farm.id;
      const config = { headers: { Authorization: token } };

      // Single API call to the dashboard endpoint
      const response = await axios.get(
        `/api/payment/hrDashboard/${farmId}?from=${fromTimestamp}&to=${toTimestamp}`, // [cite: 1078, 1079, 1080, 1081, 1082, 1083]
        config
      );

      setDashboardData(response.data?.items?.[0] || {});
    } catch (err) {
      setError(t("Failed to fetch salaries summary."));
      toast.error(t("Failed to fetch salaries summary."));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeMonth, token, t, userData]);

  useEffect(() => {
    fetchSalariesSummaryData();
  }, [fetchSalariesSummaryData]);

  // --- Action Handlers (Updated) ---
  // --- Action Handlers (Updated) ---
  const handleEdit = (transaction) => {
    const employeeId = transaction.employee?.id;
    const transactionId = transaction.payment?.id;

    if (employeeId && transactionId) {
      // This navigation pattern mirrors the implementation in EmployeeProfile.js.
      // It navigates to a dedicated edit page, passing the transaction data
      // in the navigation state to prepopulate the edit form.
      navigate(`/employees/${employeeId}/transactions/${transactionId}/edit`, {
        state: {
          transactionToEdit: transaction,
          returnPath: "/salaries-summary",
        },
      });
    } else {
      toast.error(
        t("Could not edit. Transaction or employee data is missing.")
      );
    }
  };

  const handleDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;
    try {
      // Use the ID from the nested 'payment' object
      await axios.delete(`/api/payment/${transactionToDelete.payment.id}`, {
        // [cite: 868, 869, 870, 871]
        headers: { Authorization: token },
      });
      toast.success(t("Transaction deleted successfully."));
      fetchSalariesSummaryData(); // Refresh data
    } catch (error) {
      toast.error(t("Failed to delete transaction."));
      console.error("Delete error:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setTransactionToDelete(null);
    }
  };

  const isRTL = i18n.language === "ar";
  const currency = userData?.Tenant?.currency || "EGP";

  if (loading) {
    return <div className={styles.centeredMessage}>{t("Loading...")}</div>;
  }
  if (error) {
    return <div className={styles.centeredMessage}>{error}</div>;
  }

  // Use the recentTransactions array from the dashboard data
  const transactions = dashboardData?.recentTransactions || [];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          onClick={() => navigate("/employees")}
          className={styles.backButton}
          aria-label={t("Go back")}
        >
          <FaArrowLeft
            style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
          />
        </button>
        <h1 className={styles.title}>{t("Salaries Summary")}</h1>
      </div>

      <div className={styles.monthSliderContainer}>
        <div className={styles.horizontalScrollContainer} ref={monthSliderRef}>
          {months.map((month) => (
            <button
              key={month.monthIndex}
              className={`${styles.monthTab} ${
                activeMonth === month.monthIndex ? styles.active : ""
              }`}
              data-active={activeMonth === month.monthIndex}
              onClick={() => setActiveMonth(month.monthIndex)}
            >
              {month.name}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <FaMoneyBillWave />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t("Total Paid Salaries")}</span>
            <span className={styles.cardValue}>
              {dashboardData?.totalSalariesPaid?.toLocaleString(
                i18n.language
              ) || "0.00"}{" "}
              {currency}
            </span>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <FaHandHoldingUsd />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>
              {t("Total Advances Given")}
            </span>
            <span className={styles.cardValue}>
              {dashboardData?.totalAdvancesGiven?.toLocaleString(
                i18n.language
              ) || "0.00"}{" "}
              {currency}
            </span>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <FaBalanceScale />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>
              {t("Remaining Advances Due")}
            </span>
            <span className={styles.cardValue}>
              {dashboardData?.totalOutstandingAdvances?.toLocaleString(
                i18n.language
              ) || "0.00"}{" "}
              {currency}
            </span>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}>
            <FaFileInvoice />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t("Total Deductions")}</span>
            <span className={styles.cardValue}>
              {dashboardData?.totalDeductions?.toLocaleString(i18n.language) ||
                "0.00"}{" "}
              {currency}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.transactionsSection}>
        <h2 className={styles.transactionsTitle}>
          {t("Latest Salary Transactions")}
        </h2>
        <div className={styles.transactionsContent}>
          {transactions.length > 0 ? (
            <div className={styles.transactionList}>
              {transactions.map((tx) => (
                <TransactionItem
                  key={tx.payment.id}
                  transaction={tx}
                  isRTL={isRTL}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className={styles.noTransactionsContainer}>
              <FaFileInvoiceDollar className={styles.noTransactionsIcon} />
              <p>{t("No transactions recorded for this month.")}</p>
            </div>
          )}
        </div>
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        // Pass the nested payment object to the modal if it needs it
        transaction={transactionToDelete?.payment}
      />
    </div>
  );
};

export default SalariesSummary;
